// ANA-R3-ESC-01 — React binding for the viewer escape stack. A feature
// calls this with its current active-state + interrupt handler; the hook
// keeps the registry entry alive for the component's lifetime and always
// exposes the latest `isActive`/`onEscape` via refs (so it never has to
// re-register on every state change).
import { useEffect, useRef } from "react";
import { registerEscapeLayer } from "./escapeStack.js";

export interface UseEscapeLayerOptions {
  /** Stable id for this layer (e.g. "analysis:deselect"). */
  id: string;
  /** Priority — use a value from ESCAPE_PRIORITY. */
  priority: number;
  /** Whether ESC currently has something to interrupt here. Recomputed each render. */
  isActive: boolean;
  /** Run when ESC selects this layer as the top active one. */
  onEscape: () => void;
  /** See EscapeLayer.passthrough — the layer blocks lower layers but is owned elsewhere. */
  passthrough?: boolean;
}

export function useEscapeLayer({
  id,
  priority,
  isActive,
  onEscape,
  passthrough = false,
}: UseEscapeLayerOptions): void {
  const isActiveRef = useRef(isActive);
  const onEscapeRef = useRef(onEscape);
  isActiveRef.current = isActive;
  onEscapeRef.current = onEscape;

  useEffect(() => {
    return registerEscapeLayer({
      id,
      priority,
      isActive: () => isActiveRef.current,
      handle: () => {
        onEscapeRef.current();
      },
      passthrough,
    });
    // Re-register only when the layer identity changes; the live state is
    // read through refs so value changes don't churn the registry.
  }, [id, priority, passthrough]);
}
